import type { LandingPageLayout } from '@/types/landingPage'

const layouts: Array<{ key: LandingPageLayout; label: string }> = [
    { key: 'Hero Flow', label: 'Hero Flow' },
    { key: 'Form First Hero', label: 'Form First Hero' },
    { key: 'Sticky Sidebar', label: 'Sticky Sidebar' },
    { key: 'Brand Native', label: 'Brand Native' },
]

/** Wireframe thumbnail placeholders for each layout */
function LayoutWireframe({ layout }: { layout: LandingPageLayout }) {
    const base = 'w-full h-20 rounded border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 flex overflow-hidden'

    switch (layout) {
        case 'Hero Flow':
            return (
                <div className={base}>
                    <div className="flex flex-col w-full">
                        <div className="h-10 bg-slate-300 dark:bg-slate-500 rounded-t" />
                        <div className="flex-1 flex items-center justify-center gap-1 p-1">
                            <div className="w-1/3 h-4 bg-slate-200 dark:bg-slate-600 rounded" />
                            <div className="w-1/3 h-4 bg-slate-200 dark:bg-slate-600 rounded" />
                            <div className="w-1/3 h-4 bg-slate-200 dark:bg-slate-600 rounded" />
                        </div>
                    </div>
                </div>
            )
        case 'Form First Hero':
            return (
                <div className={base}>
                    <div className="flex w-full">
                        <div className="w-2/5 bg-slate-200 dark:bg-slate-600 flex items-center justify-center p-1">
                            <div className="w-full h-10 border border-dashed border-slate-400 dark:border-slate-500 rounded" />
                        </div>
                        <div className="w-3/5 bg-slate-300 dark:bg-slate-500" />
                    </div>
                </div>
            )
        case 'Sticky Sidebar':
            return (
                <div className={base}>
                    <div className="w-1/3 bg-slate-300 dark:bg-slate-500 border-r border-slate-200 dark:border-slate-600" />
                    <div className="w-2/3 flex flex-col gap-1 p-1">
                        <div className="h-3 bg-slate-200 dark:bg-slate-600 rounded" />
                        <div className="h-3 bg-slate-200 dark:bg-slate-600 rounded" />
                        <div className="h-3 bg-slate-200 dark:bg-slate-600 rounded w-2/3" />
                    </div>
                </div>
            )
        case 'Brand Native':
            return (
                <div className={base}>
                    <div className="flex flex-col w-full p-1 gap-1">
                        <div className="h-3 bg-primary-200 dark:bg-primary-700 rounded w-1/2" />
                        <div className="flex-1 bg-slate-200 dark:bg-slate-600 rounded" />
                        <div className="h-3 bg-primary-200 dark:bg-primary-700 rounded w-1/3" />
                    </div>
                </div>
            )
        default:
            return <div className={base} />
    }
}

interface LayoutSelectorProps {
    selected: LandingPageLayout
    onSelect: (layout: LandingPageLayout) => void
    disabled?: boolean
    compact?: boolean
}

export function LayoutSelector({ selected, onSelect, disabled, compact }: LayoutSelectorProps) {
    return (
        <div className={compact ? 'grid grid-cols-1 gap-2' : 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3'}>
            {layouts.map(({ key, label }) => (
                <button
                    key={key}
                    type="button"
                    disabled={disabled}
                    onClick={() => onSelect(key)}
                    className={`
                        ${compact ? 'flex flex-row items-center gap-3 p-2' : 'flex flex-col items-center gap-2 p-3'} rounded-lg border-2 transition-all text-sm font-medium
                        ${selected === key
                            ? 'border-primary-600 dark:border-primary-500 ring-2 ring-primary-200 dark:ring-primary-800 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                            : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600'
                        }
                        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                >
                    <div className={compact ? 'w-16 shrink-0' : 'w-full'}>
                        <LayoutWireframe layout={key} />
                    </div>
                    <span className={compact ? 'text-xs' : ''}>{label}</span>
                </button>
            ))}
        </div>
    )
}
